import React, { Suspense, useEffect, useState } from "react";

import {
	RouteComponentProps,
	useRouteMatch,
	withRouter
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import axios from "axios";

import "./Dashboard.less";

import {
	AuthToken,
	cache,
	ZomentumRoutes,
	ZomentumStorageKeys,
	ZomentumWindow
} from "@/Contracts";

import LoginActionCreators from "@/Actions/Login";
import UsersActionCreators from "@/Actions/User";

import { DashboardLayout } from "../DashboardLayout";
import ZContentLoadingIcon from "@atoms/ZContentLoadingIcon";

import { UIConfigActionCreators } from "@/Actions/UI";

import PreSetupAccountLoadThirdPartyScripts from "./PreSetupAccountLoadThirdPartyScripts";
import PostSetupAccountLoadThirdPartyScripts from "./PostSetupAccountLoadThirdPartyScripts";
import ZMessage from "@molecules/ZMessage";
import { Auth0ContextInterface, useAuth0 } from "@auth0/auth0-react";
import SalesActivityDrawers from "@/SalesActivity/pages/Drawers/SalesActivities";
import Banner from "../Banner";
import CancelledAccountModal from "@pages/Authentication/modals/CancelledAccountModal";
import OnBoardingModal from "../OnBoardingModal";
import { EditProductToQuoteBlockDrawer } from "@/Components/DocumentsPage/Drawers/DocumentProductDrawers/EditProductToQuoteBlockDrawer";
import { setupDone } from "@/V2Utils";
import { fetchHostRedirectionUrl } from "@/Contracts/Utils";
import { cacheConstant } from "@/Contracts/appCache";
import useMobileView from "@/Hooks/useMobileView";
import { Auth0LoginState } from "@/Contracts/Login";
import { GlobalState } from "@/Reducers/interface";
import { ZModal } from "@atoms";

import ZPaymentDueBanner from "@/V2Components/UI/organisms/ZPaymentDueBanner";
import { installApiInterceptors } from "./installApiInterceptors";
import PageTitleManager from "./PageTitle/PageTitleManager";
import { getAppPathname } from "@/Utils/routing/getAppPathname";
import { isEmbedded } from "@/Utils/superops/embedDetection";

const PartnerDashboardRoutes = React.lazy(
	() => import("./DashboardRoutes/PartnerDashboardRoutes")
);

declare let window: ZomentumWindow;

if (process.env.REACT_APP_SHA) {
	window.REACT_APP_SHA = process.env.REACT_APP_SHA;
}

export interface Props extends RouteComponentProps {
	updateServiceWorker: (registration: ServiceWorkerRegistration) => void;
	auth0LoginState: Auth0LoginState;
}

export const AppDashboard: React.FC<Props> = props => {
	const auth0 = useAuth0();
	const dispatch = useDispatch();
	const logout = (auth0: Auth0ContextInterface) =>
		dispatch(LoginActionCreators.logout(auth0));

	const isOnAuthorize =
		useRouteMatch(ZomentumRoutes.Authorize)?.isExact ?? false;
	const sessionInitialized = useSelector(
		(state: GlobalState) => state.login.sessionInitialized
	);

	const [tokenReady, setTokenReady] = useState(false);
	useEffect(() => {
		if (auth0.isLoading) return;
		// While /authorize is mounted, Authorize.tsx owns the bootstrap and
		// dispatches sessionInitialized on success. Dashboard must not race
		// it with a duplicate auth'd fetch against a stale cached token.
		// The ~8 redundant API calls in HAR phase 1 came from this race.
		// Still flip tokenReady true so PartnerDashboardRoutes renders and
		// the Authorize route component actually mounts — skipping only the
		// dispatch work, not the render gate.
		if (isOnAuthorize) {
			setTokenReady(true);
			return;
		}
		if (auth0.isAuthenticated) {
			(async () => {
				let token: string;
				try {
					token = await auth0.getAccessTokenSilently();
				} catch (err) {
					console.error("[Auth] getAccessTokenSilently failed:", err);
					if (isEmbedded()) {
						ZMessage.error(
							"Session expired. Please reload the SuperOps page."
						);
						setTokenReady(true);
						return;
					}
					auth0.loginWithRedirect({
						appState: {
							return_to_url:
								window.location.pathname +
								window.location.search
						}
					});
					return;
				}

				const authToken = new AuthToken(token);
				cache.save(ZomentumStorageKeys.Token, authToken);

				const cachedConfig = cache.get(
					cacheConstant.configurationSettings,
					true
				);
				const hostRedirectionUrl = fetchHostRedirectionUrl(
					authToken,
					cachedConfig
				);
				if (hostRedirectionUrl) {
					window.location.href = hostRedirectionUrl;
					return;
				}

				// Skip the duplicate getInitialConfig if /authorize already
				// populated the store via bootstrapAuthenticatedSession.
				// sessionInitialized is an explicit Redux flag set only on
				// successful bootstrap, NOT a truthiness probe on
				// configurationSettings (which defaults to a truthy empty
				// object from day one and is not a valid readiness signal).
				if (!sessionInitialized) {
					try {
						await dispatch(
							UsersActionCreators.getInitialConfig() as any
						);
					} catch (err) {
						console.error("[Config] getInitialConfig failed:", err);
					}
				}

				setTokenReady(true);
			})();
		} else {
			const hint = new URLSearchParams(window.location.search).get(
				"id_token_hint"
			);

			if (hint && isEmbedded()) {
				const stripped = new URLSearchParams(window.location.search);
				stripped.delete("id_token_hint");
				const rawReturnTo =
					window.location.pathname +
					(stripped.toString() ? "?" + stripped.toString() : "");
				const returnTo = getAppPathname(rawReturnTo);
				if (returnTo && returnTo !== "/") {
					cache.save(ZomentumStorageKeys.RETURN_TO_URL, returnTo);
				}
				setTokenReady(true);

				const region = new URLSearchParams(window.location.search).get(
					"region"
				);
				const authorizeUrl =
					`${
						ZomentumRoutes.Authorize
					}?superops_hint=${encodeURIComponent(hint)}` +
					(region ? `&region=${encodeURIComponent(region)}` : "");

				props.history.push(authorizeUrl);
				return;
			}
			setTokenReady(true);
		}
	}, [
		auth0.isLoading,
		auth0.isAuthenticated,
		isOnAuthorize,
		sessionInitialized
	]);

	const isMobileView = useMobileView();

	const isPdfView = useRouteMatch(ZomentumRoutes.DocumentsPdfView)?.isExact;
	const {
		currentActiveOpportunity,
		currentActiveClient,
		userData,
		currentActiveReport,
		currentActivePipeline,
		favicon
	} = useSelector((state: GlobalState) => ({
		currentActiveOpportunity: state.opportunities.currentActiveOpportunity,
		currentActiveClient: state.clients.currentActiveClient,
		userData: state.user.userData,
		currentActiveReport: state.reports.currentActiveReport,
		currentActivePipeline: state.pipelines.currentActivePipeline,
		favicon: state.user.userCompany.favicon
	}));

	const [isPasswordlessError, setIsPasswordlessError] = useState(false);

	const showPasswordLessLinkExpired = () => {
		setIsPasswordlessError(true);

		// show modal error
		ZModal.error({
			title: "Signup Link Expired",
			content:
				"The link you are trying to access has expired. We have resent the activation link on your registered email id.",
			onOk: () => {
				logout(auth0);
			},
			onCancel: () => {
				logout(auth0);
			},
			closable: false,
			maskClosable: false
		});
	};

	const checkForPasswordLessError = async () => {
		const error = window.location.hash;
		if (error.includes("unauthorized") && !auth0.isAuthenticated) {
			showPasswordLessLinkExpired();
		}
	};

	useEffect(() => {
		const currFavicon = document.getElementById(
			"favicon"
		) as HTMLLinkElement;
		if (favicon && currFavicon) {
			currFavicon.href = favicon;
		}
	}, []);

	useEffect(() => {
		if (!auth0.isLoading) checkForPasswordLessError();
	}, [auth0.isLoading]);

	useEffect(() => {
		if (
			props.auth0LoginState &&
			Object.keys(props.auth0LoginState)?.length !== 0
		) {
			dispatch(
				LoginActionCreators.auth0LoginStateReceived(
					props.auth0LoginState
				)
			);
		}
	}, [props.auth0LoginState]);

	useEffect(() => {
		dispatch(UIConfigActionCreators.updateMobileView(isMobileView));
	}, [isMobileView]);

	useEffect(() => {
		return installApiInterceptors(axios, {
			getAccessToken: () => auth0.getAccessTokenSilently(),
			logout: () => logout(auth0),
			history: props.history,
			reportData: {
				userData,
				currentActiveOpportunity,
				currentActiveReport,
				currentActivePipeline,
				currentActiveClient
			}
		});
	}, []);

	const { configurationSettings } = useSelector((state: GlobalState) => ({
		configurationSettings: state.user.configurationSettings
	}));

	if (!tokenReady) return <ZContentLoadingIcon />;

	return (
		<>
			{!isPdfView && !isOnAuthorize && <Banner />}
			{!isOnAuthorize && <ZPaymentDueBanner />}
			{/* <AppUpdateBanner updateServiceWorker={props.updateServiceWorker} /> */}
			{!isPdfView && !isOnAuthorize && (
				<>
					<CancelledAccountModal />
					<OnBoardingModal />
				</>
			)}
			<PageTitleManager />
			<DashboardLayout updateServiceWorker={props.updateServiceWorker}>
				<Suspense fallback={<ZContentLoadingIcon />}>
					{!isPasswordlessError && <PartnerDashboardRoutes />}
				</Suspense>

				{!isPdfView && <SalesActivityDrawers />}
				{!isPdfView && (
					<>
						<EditProductToQuoteBlockDrawer />
					</>
				)}
			</DashboardLayout>
			{!isOnAuthorize && <PreSetupAccountLoadThirdPartyScripts />}
			{setupDone(configurationSettings) && !isOnAuthorize && (
				<PostSetupAccountLoadThirdPartyScripts />
			)}
		</>
	);
};

export default withRouter((props: Props) => {
	return <AppDashboard {...props} />;
});
